/**
 * Build fingerprint + stale-bundle detection.
 *
 * A prime suspect for a "recording silently uploaded nothing" report is a
 * student running a STALE cached bundle (e.g. the old high-bitrate build) while
 * we think a fix is live. There is no build-time define here (the bundler config
 * is out of scope), so the fingerprint is derived at runtime from the hashed
 * entry-chunk filename Vite emits — it changes whenever the code changes. In dev
 * (unhashed `/src/main.tsx`) it reads "dev" and update-checks are inert.
 */
import { useEffect, useState } from "react";

function entryScriptSrc(): string | null {
  if (typeof document === "undefined") return null;
  const srcs = Array.from(
    document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]'),
  )
    .map((s) => s.getAttribute("src"))
    .filter((s): s is string => s !== null);
  const hashed = srcs.find((src) => /\/assets\/index-[^/]+\.js/.test(src));
  return hashed ?? srcs[0] ?? null;
}

function hashFrom(src: string | null): string {
  if (!src) return "unknown";
  const m = src.match(/index-([A-Za-z0-9_-]+)\.js/);
  if (m?.[1]) return m[1];
  return /\/src\/main\.tsx/.test(src) ? "dev" : "unknown";
}

/** The running bundle's fingerprint — logged and shown small on student pages. */
export const BUILD_ID = hashFrom(entryScriptSrc());

/** Whether the server is now serving a DIFFERENT bundle than the one running. */
export async function isNewVersionAvailable(): Promise<boolean> {
  if (BUILD_ID === "dev" || BUILD_ID === "unknown") return false;
  try {
    const res = await fetch(`${window.location.origin}/?_v=${Date.now()}`, {
      cache: "no-store",
    });
    const html = await res.text();
    const m = html.match(/\/assets\/index-([A-Za-z0-9_-]+)\.js/);
    const latest = m?.[1] ?? null;
    return latest !== null && latest !== BUILD_ID;
  } catch {
    return false;
  }
}

/**
 * Logs the build id once, then watches for a newer bundle (on focus + every few
 * minutes) so a student on a stale bundle can be prompted to reload.
 */
export function useVersionWatch(): {
  buildId: string;
  updateAvailable: boolean;
} {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    console.info(`[exam] build ${BUILD_ID}`);
    let cancelled = false;
    const check = () =>
      void isNewVersionAvailable().then((v) => {
        if (!cancelled && v) setUpdateAvailable(true);
      });

    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    const id = setInterval(check, 5 * 60_000);
    check();

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      clearInterval(id);
    };
  }, []);

  return { buildId: BUILD_ID, updateAvailable };
}
